import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheck, FiClock, FiSave, FiShield, FiUser } = FiIcons;

const CHECKS = [
  { id: 'legal_name', label: 'Legal name checked against identification' },
  { id: 'contact', label: 'Contact details confirmed independently' },
  { id: 'relationship', label: 'Relationship or beneficiary designation reviewed' },
  { id: 'scope', label: 'Intended document access reviewed' },
];

export default function BeneficiaryCheckCard({ entry, onSave }) {
  const { document, party } = entry;
  const persistedChecks = JSON.stringify(party?.beneficiary_checks?.checks ?? {});
  const [checks, setChecks] = useState(() => JSON.parse(persistedChecks));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const complete = CHECKS.every(check => checks?.[check.id] === true);
  const reviewedAt = party?.beneficiary_checks?.reviewed_at;

  useEffect(() => {
    setChecks(JSON.parse(persistedChecks));
    setSaved(false);
    setError('');
  }, [document?.id, entry.index, persistedChecks]);

  const toggleCheck = (id) => {
    setChecks(current => ({ ...current, [id]: !current?.[id] }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(entry, checks);
      setSaved(true);
    } catch (err) {
      setError(err?.message || 'Could not save this review.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-2xl border border-brand-dark bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-700">
          <SafeIcon icon={FiUser} className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="truncate text-sm font-bold text-accent-navy">{party?.name || 'Unnamed beneficiary'}</h4>
              <p className="text-xs text-text-muted">{party?.email || 'No email listed'}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${
              complete ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {complete ? 'Review complete' : 'Review pending'}
            </span>
          </div>
          <p className="mt-2 truncate text-[10px] text-text-muted">
            {document?.original_name || 'Vault document'}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {CHECKS.map(check => (
          <label key={check.id} className="flex cursor-pointer items-start gap-2.5 rounded-xl bg-brand-light/60 p-2.5 text-xs text-accent-navy">
            <input
              type="checkbox"
              checked={checks?.[check.id] === true}
              onChange={() => toggleCheck(check.id)}
              className="mt-0.5 h-4 w-4 accent-emerald-600"
            />
            <span>{check.label}</span>
          </label>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[10px] text-text-muted">
          <SafeIcon icon={reviewedAt && complete ? FiCheck : FiClock} className="h-3.5 w-3.5" />
          {reviewedAt && complete
            ? `Completed ${new Date(reviewedAt).toLocaleDateString()}`
            : `${CHECKS.filter(check => checks?.[check.id] === true).length} of ${CHECKS.length} checks selected`}
        </p>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-xl bg-accent-navy px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white transition hover:bg-accent-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <SafeIcon icon={FiSave} className={`h-3.5 w-3.5 ${saving ? 'animate-pulse' : ''}`} />
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save review'}
        </button>
      </div>

      {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}
    </article>
  );
}